/**
 * Feature flag service.
 *
 * Resolves boolean flags per profile from `GET /api/flags`. Used to gate
 * the messaging-spine rollout (Phase A → D). Flags are loaded once at app
 * boot after the user is authenticated and refreshed lazily on demand.
 *
 * All flags here default to `false` if the backend hasn't returned a
 * value — that matches the rollout posture of "off in production until
 * each phase passes its ACs."
 */

import { apiService } from './api/apiService';

export type FlagKey =
  | 'messaging.persistence_v2'
  | 'messaging.anonymous'
  | 'messaging.anonymous.rooms'
  | 'messaging.disappearing_text'
  | 'messaging.disappearing_images'
  // Profiles & Social Connections — each phase gated independently, all
  // default off. Each flag assumes the prior phase's flag is on (the card
  // degrades to a no-connect-button projection when `social.graph` is off).
  // `social.dm_presence` additionally requires `messaging.persistence_v2` on
  // (connection DMs ride the existing message spine).
  | 'social.profile_card'   // Phase A — unified card projection + visibility
  | 'social.graph'          // Phase B — request/accept/decline/block/mute
  | 'social.dm_presence'    // Phase C — connection DMs + presence
  | 'social.invites_feed';  // Phase D — cross-area invites + activity feed

export type FlagSnapshot = Partial<Record<FlagKey, boolean>>;

type Listener = (snapshot: FlagSnapshot) => void;

class FeatureFlagService {
  private snapshot: FlagSnapshot = {};
  private listeners = new Set<Listener>();
  private loadingPromise: Promise<void> | null = null;
  private loadedForProfileId: string | null = null;

  /**
   * Fetch the per-profile flag snapshot. Subsequent calls for the same
   * profile reuse the in-flight promise. Switching profiles forces a
   * fresh load.
   */
  async load(profileId: string): Promise<void> {
    if (this.loadedForProfileId === profileId && !this.loadingPromise) return;
    if (this.loadingPromise && this.loadedForProfileId === profileId) {
      return this.loadingPromise;
    }

    this.loadedForProfileId = profileId;
    this.loadingPromise = this.fetch().finally(() => {
      this.loadingPromise = null;
    });
    return this.loadingPromise;
  }

  private async fetch(): Promise<void> {
    const response = await apiService.get<FlagSnapshot>('/api/flags');
    if (response.error || !response.data) {
      // Fail closed — keep all flags off rather than guessing. The
      // existing legacy chat code paths continue to serve traffic.
      this.snapshot = {};
    } else {
      this.snapshot = response.data;
    }
    this.emit();
  }

  /**
   * Sync read of a flag. Returns false until `load()` has resolved.
   */
  isOn(key: FlagKey): boolean {
    return this.snapshot[key] === true;
  }

  /**
   * Force-set a flag locally (test / dev only).
   */
  override(key: FlagKey, value: boolean): void {
    this.snapshot = { ...this.snapshot, [key]: value };
    this.emit();
  }

  /**
   * Reset to "no profile loaded" state — call on sign-out so the next
   * sign-in re-fetches.
   */
  reset(): void {
    this.snapshot = {};
    this.loadedForProfileId = null;
    this.loadingPromise = null;
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    // If something has already told us which profile to load for but
    // load() was never called (e.g. forgotten in the boot sequence), do
    // a lazy fetch on first subscribe rather than silently leaving every
    // flag at false. Idempotent — load() short-circuits if already loaded.
    if (this.loadedForProfileId && Object.keys(this.snapshot).length === 0) {
      void this.fetch();
    }
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Set a profile id without triggering a fetch — used by the auth flow
   * before it calls load(). If a component subscribes before load() is
   * called, subscribe() will lazy-fetch using this id.
   */
  setProfile(profileId: string | null): void {
    this.loadedForProfileId = profileId;
  }

  private emit(): void {
    for (const l of this.listeners) l(this.snapshot);
  }
}

export const featureFlags = new FeatureFlagService();
